/**
 * PostHog analytics layer for the Pearl Vendor App.
 *
 * Mirrors the structure used by the Pearl Planner app so event names,
 * property shapes, and dedupe semantics line up across both apps and we can
 * union them in PostHog without renormalizing.
 *
 * Gating rules (intentional):
 *   - Disabled in tests (NODE_ENV === 'test')
 *   - Disabled if REACT_APP_POSTHOG_KEY is missing (no-op, no warnings)
 *   - In non-production builds, additionally requires
 *     REACT_APP_POSTHOG_ENABLE_ANALYTICS=true so local dev doesn't pollute
 *     the production project. When that flag is off, calls log to console
 *     with a [Analytics - Dev] prefix instead.
 *   - Gated on OneTrust C0002 (Performance) consent. PostHog is initialized
 *     opted out, so nothing is captured and no cookie is written until
 *     consent is confirmed; see lib/consent.js for the CMP-absent rule.
 */

import posthog from 'posthog-js';
import { hasAnalyticsConsent } from './consent';
import { getDeployEnvironment } from './environment';

const ANALYTICS_EVENTS = Object.freeze({
    VENDOR_VIEWED: 'vendor_viewed',
    VENDOR_PROFILE_VIEWED: 'vendor_profile_viewed',
    VENDOR_WEBSITE_CLICKED: 'vendor_website_clicked',
    VENDOR_SOCIAL_CLICKED: 'vendor_social_clicked',
    // Pearl marketing landing page (route '/'), instrumented ahead of a
    // redesign to learn scroll depth and which feature CTAs convert.
    LANDING_PAGE_VIEWED: 'landing_page_viewed',
    LANDING_PAGE_SCROLLED: 'landing_page_scrolled',
    LANDING_CTA_CLICKED: 'landing_cta_clicked',
});

/**
 * Canonical social_platform values for VENDOR_SOCIAL_CLICKED, so PostHog
 * breakdowns don't split on inconsistent casing/naming per call site.
 */
const VENDOR_SOCIAL_PLATFORMS = Object.freeze({
    YOUTUBE: 'youtube',
    GOOGLE: 'google',
    PINTEREST: 'pinterest',
    FACEBOOK: 'facebook',
    TIKTOK: 'tiktok',
    INSTAGRAM: 'instagram',
    LOVE_STORIES_TV: 'love_stories_tv',
});

/**
 * Where a landing-page CTA sits on the page. Frozen so PostHog breakdowns
 * have a canonical value list instead of ad-hoc strings per call site.
 */
const LANDING_CTA_SECTIONS = Object.freeze({
    HERO: 'hero',
    FEATURE_CARD: 'feature_card',
    WEDDING_PRO: 'wedding_pro',
});

/**
 * Which signup funnel a CTA enters. Only set on CTAs that actually start a
 * signup — see LANDING_CTAS for why that distinction matters.
 */
const LANDING_SIGNUP_AUDIENCES = Object.freeze({
    COUPLE: 'couple',
    VENDOR: 'vendor',
});

const VENDOR_VIEW_SOURCES = Object.freeze({
    SEARCH: 'search',
    RECOMMENDATIONS: 'recommendations',
    SAVED_VENDORS: 'saved_vendors',
    MAP: 'map',
    DIRECTORY_NATIONAL: 'directory_national',
    DIRECTORY_LOCAL: 'directory_local',
    // Origin when there's no in-app referrer: SEO/direct entry, refresh, or
    // an external link straight to a profile.
    DIRECT: 'direct',
});

/**
 * Canonicalize the many shapes vendor_category arrives in so category
 * breakdowns in PostHog don't split into near-duplicate values.
 *
 * Handles: a plain string ("Florist"), a comma-joined string
 * ("Florist, DJ" — how claimed webstores store multi-category), a real
 * array (["Florist", "DJ"]), and a JSON-stringified array ('["Florist"]'
 * — how some search-result records carry categoryDetails.name).
 *
 * Returns { primary, all }: primary is the first canonical category (a
 * single string for clean breakdowns), all is the full list. Never throws —
 * it runs inside the analytics path and must not break product flows.
 */
const normalizeVendorCategory = (raw) => {
    try {
        if (raw === null || raw === undefined) return { primary: undefined, all: [] };

        let list;
        if (Array.isArray(raw)) {
            list = raw;
        } else if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed === '') return { primary: undefined, all: [] };
            if (trimmed.startsWith('[')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    list = Array.isArray(parsed) ? parsed : [trimmed];
                } catch {
                    // Malformed JSON-ish string — treat as a plain value.
                    list = [trimmed];
                }
            } else {
                list = trimmed.split(',');
            }
        } else {
            list = [raw];
        }

        const all = list
            .map((c) => (typeof c === 'string' ? c.trim() : c))
            .filter((c) => c !== null && c !== undefined && c !== '');

        return { primary: all.length > 0 ? all[0] : undefined, all };
    } catch {
        return { primary: undefined, all: [] };
    }
};

const isTest = process.env.NODE_ENV === 'test';
const isProduction = process.env.NODE_ENV === 'production';
const hasKey = !!process.env.REACT_APP_POSTHOG_KEY;
const devOptIn = process.env.REACT_APP_POSTHOG_ENABLE_ANALYTICS === 'true';

const shouldSendToPostHog = () => {
    if (isTest) return false;
    if (!hasKey) return false;
    if (!isProduction && !devOptIn) return false;
    return true;
};

let initialized = false;

const initAnalytics = () => {
    if (initialized || isTest) return;
    if (!hasKey) return;
    if (!isProduction && !devOptIn) {
        initialized = true;
        return;
    }

    try {
        posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
            api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
            // We emit explicit events; autocapture creates a lot of noise we don't want.
            autocapture: false,
            // 'history_change' rather than true: this is a react-router SPA, so
            // plain `true` would only capture the initial load and miss every
            // client-side route change.
            //
            // This is also what produces scroll depth. PostHog only attaches
            // $prev_pageview_max_scroll_percentage (and friends) to $pageleave
            // when a $pageview established the prior-page context first — with
            // capture_pageview off, every $pageleave carried none of it.
            capture_pageview: 'history_change',
            // capture_pageleave is intentionally left at its default of
            // 'if_capture_pageview'. Forcing it to true was what produced
            // $pageleave events with no matching $pageview, which made session,
            // bounce and time-on-page unreliable for this app.
            persistence: 'localStorage+cookie',
            disable_session_recording: true,
            // Start opted out and let consent turn capture on. Both flags are
            // required: opt_out_capturing_by_default alone stops events but
            // still writes the identity cookie, which is the thing consent is
            // meant to prevent.
            opt_out_capturing_by_default: true,
            opt_out_persistence_by_default: true,
            // Store the opt-out decision in a cookie rather than localStorage
            // so it is shared across *.davidsbridal.com instead of being
            // re-decided per subdomain.
            opt_out_capturing_persistence_type: 'cookie',
            loaded: (ph) => {
                if (!isProduction) {
                    ph.debug();
                }
            },
        });
        initialized = true;
        applyConsent();
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Analytics] PostHog init failed', err);
    }
};

/**
 * Opt PostHog in or out to match the current consent state. Safe to call
 * repeatedly; PostHog treats a redundant opt-in/opt-out as a no-op.
 */
let pageviewCapturedSinceOptIn = false;

const applyConsent = () => {
    if (!shouldSendToPostHog() || !initialized) return;
    try {
        if (hasAnalyticsConsent()) {
            posthog.opt_in_capturing();
            // PostHog suppresses the initial $pageview while opted out and does
            // not replay it once consent arrives, so the entry page would end up
            // with a $pageleave and no matching $pageview — which is precisely
            // the case where scroll depth is lost, because PostHog only attaches
            // scroll properties when a prior $pageview established the context.
            // Subsequent route changes are covered by capture_pageview:
            // 'history_change'; this only backfills the first one.
            if (!pageviewCapturedSinceOptIn) {
                pageviewCapturedSinceOptIn = true;
                posthog.capture('$pageview');
            }
        } else {
            pageviewCapturedSinceOptIn = false;
            posthog.opt_out_capturing();
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Analytics] consent sync failed', err);
    }
};

/**
 * Re-evaluate consent after the CMP loads or the visitor changes their choice.
 * Initializes PostHog first if consent arrived before anything was tracked.
 */
const syncAnalyticsConsent = () => {
    if (!shouldSendToPostHog()) return;
    if (!initialized) initAnalytics();
    applyConsent();
};

const getSessionId = () => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return undefined;
        const KEY = 'pearl_analytics_session_id';
        let id = window.sessionStorage.getItem(KEY);
        if (!id) {
            id =
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
            window.sessionStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        return undefined;
    }
};

const getBaseProperties = () => ({
    app: 'pearl-vendor-app',
    // The deploy target, NOT NODE_ENV: `craco build` forces NODE_ENV to
    // 'production' for every deploy, so test/UAT/sandbox traffic was
    // indistinguishable from real production. See lib/environment.js.
    environment: getDeployEnvironment(),
    // Lets analysts separate builds from before this fix, whose `environment`
    // is 'production' regardless of where they actually ran.
    app_version: process.env.REACT_APP_BUILD_TAG || undefined,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
});

const trackEvent = (eventName, properties = {}) => {
    if (!eventName) return;

    const payload = {
        ...getBaseProperties(),
        ...properties,
    };

    try {
        if (shouldSendToPostHog()) {
            if (!initialized) initAnalytics();
            posthog.capture(eventName, payload);
        } else if (!isTest) {
            // eslint-disable-next-line no-console
            console.log('[Analytics - Dev]', eventName, payload);
        }
    } catch (err) {
        // Never let analytics break product flows.
        // eslint-disable-next-line no-console
        console.warn('[Analytics] trackEvent failed', eventName, err);
    }
};

const identifyUser = (userId, traits = {}) => {
    if (!userId) return;
    try {
        if (shouldSendToPostHog()) {
            if (!initialized) initAnalytics();
            posthog.identify(String(userId), traits);
        } else if (!isTest) {
            // eslint-disable-next-line no-console
            console.log('[Analytics - Dev] identify', userId, traits);
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Analytics] identifyUser failed', err);
    }
};

/**
 * Whether PostHog currently holds an *identified* (non-anonymous) distinct_id.
 *
 * Read from PostHog's own persisted state, so it survives a reload — an
 * in-memory flag would forget on every mount and miss a prior session's
 * identity persisting into an expired one. Falls back to false when the
 * internal is unavailable, which biases toward never resetting: a spurious
 * reset is the more damaging failure.
 */
const isAnalyticsIdentified = () => {
    if (!shouldSendToPostHog() || !initialized) return false;
    try {
        return typeof posthog._isIdentified === 'function' && posthog._isIdentified() === true;
    } catch {
        return false;
    }
};

/**
 * Reset identity on a genuine logged-in -> logged-out transition only.
 *
 * Guarding on isAnalyticsIdentified() matters for two separate reasons:
 *   - An unguarded reset ran on every anonymous mount, minting a new
 *     distinct_id per page load and orphaning events onto dead identities.
 *   - reset() also clears PostHog's stored consent. With
 *     opt_out_capturing_by_default enabled, that silently turns capturing
 *     back off after the consent gate had opted us in. Consent is therefore
 *     re-applied after any reset we do perform.
 */
const resetUser = () => {
    if (!isAnalyticsIdentified()) return;
    try {
        posthog.reset();
        applyConsent();
    } catch {
        // ignore
    }
};

export {
    ANALYTICS_EVENTS,
    VENDOR_VIEW_SOURCES,
    VENDOR_SOCIAL_PLATFORMS,
    LANDING_CTA_SECTIONS,
    LANDING_SIGNUP_AUDIENCES,
    normalizeVendorCategory,
    initAnalytics,
    syncAnalyticsConsent,
    trackEvent,
    identifyUser,
    resetUser,
    getSessionId,
    getBaseProperties,
};
